import { ApplicationConfig, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import {
  provideClientHydration,
  withEventReplay,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { environment } from '../environments/environment';
import { loggingInterceptor } from './interceptors/logging-interceptor';
import { dataViewInterceptor } from './interceptors/data-view-interceptor';
import { authInterceptor } from './interceptors/auth-interceptor';
import { errorInterceptor } from './interceptors/error-interceptor';
import { kycGateInterceptor } from './interceptors/kyc-gate-interceptor';
import { routes } from './app.routes';

/**
 * Brand preset — recolors PrimeNG's `primary` palette to the Arogya blue (#007cc2)
 * so p-select, p-datepicker, p-dialog accents, buttons, etc. all match the app brand.
 */
const ArogyaPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#e6f3fa',
      100: '#cce7f4',
      200: '#99cfe9',
      300: '#66b7de',
      400: '#339fd3',
      500: '#007cc2',
      600: '#0070ae',
      700: '#005c93',
      800: '#004a76',
      900: '#00395a',
      950: '#00283f',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    // Dev: disable the SSR HTTP transfer cache so EVERY GET is a real browser
    // network call (visible in the DevTools Network tab). Prod keeps the cache.
    provideClientHydration(
      withEventReplay(),
      ...(environment.production ? [] : [withNoHttpTransferCache()]),
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: ArogyaPreset,
        options: {
          // Keep PrimeNG's generated styles below app utility classes so they stay overridable.
          cssLayer: { name: 'primeng', order: 'app-styles, primeng' },
          darkModeSelector: '.dark',
        },
      },
    }),
    // loggingInterceptor is FIRST so its timing brackets the whole chain (dev-only, no-op in prod).
    // kycGateInterceptor is registered LAST so it sees the 403 first and can swallow the
    // KYC-gate response before the generic errorInterceptor toasts "Access denied".
    provideHttpClient(
      withFetch(),
      withInterceptors([loggingInterceptor, dataViewInterceptor, authInterceptor, errorInterceptor, kycGateInterceptor]),
    ),
  ],
};
